import { useEffect, useRef, useState } from 'preact/hooks'
import styles from '../ui.module.css'

const COPIED_MS = 1500

/**
 * The private room code with a copy button (moved from the PHASE1 lobby, PHASE3 T3.2), plus T2.7's
 * "Copy link" for the `/r/<CODE>` share link when `link` is given (O-21). `class` lets the
 * matchmaking screen size it.
 */
export function CopyCode({
  code,
  link,
  class: cls = '',
}: {
  code: string
  link?: string | undefined
  class?: string | undefined
}) {
  const [copied, setCopied] = useState<'idle' | 'ok' | 'fail'>('idle')
  const [linkCopied, setLinkCopied] = useState<'idle' | 'ok' | 'fail'>('idle')
  useEffect(() => {
    if (linkCopied === 'idle') return
    const t = setTimeout(() => setLinkCopied('idle'), COPIED_MS)
    return () => clearTimeout(t)
  }, [linkCopied])
  useEffect(() => {
    if (copied === 'idle') return
    const t = setTimeout(() => setCopied('idle'), COPIED_MS)
    return () => clearTimeout(t)
  }, [copied])
  const text = useRef<HTMLSpanElement>(null)
  const copy = (): void => {
    // The Clipboard API is blocked in some iframes and unfocused pages: fall back to selecting
    // the code and `execCommand('copy')`, and leave it selected for Ctrl+C if that fails too.
    const legacy = (): void => {
      const sel = window.getSelection()
      if (text.current && sel) sel.selectAllChildren(text.current)
      const ok = document.execCommand('copy')
      if (ok) sel?.removeAllRanges()
      setCopied(ok ? 'ok' : 'fail')
    }
    const clip = navigator.clipboard as Clipboard | undefined
    if (!clip) legacy()
    else clip.writeText(code).then(() => setCopied('ok'), legacy)
  }
  // No selectable text holds the link, so a blocked clipboard just says so.
  const copyLink = (): void => {
    const clip = navigator.clipboard as Clipboard | undefined
    if (!link || !clip) setLinkCopied('fail')
    else
      clip.writeText(link).then(
        () => setLinkCopied('ok'),
        () => setLinkCopied('fail'),
      )
  }
  return (
    <div class={`${styles.code} ${cls}`}>
      <span ref={text} class={styles.codeText} aria-label={`Room code ${code.split('').join(' ')}`}>
        {code}
      </span>
      <button type="button" class={`${styles.button} ${styles.small}`} onClick={copy}>
        {copied === 'ok' ? 'Copied!' : copied === 'fail' ? 'Copy failed' : 'Copy'}
      </button>
      {link && (
        <button type="button" class={`${styles.button} ${styles.small}`} onClick={copyLink}>
          {linkCopied === 'ok'
            ? 'Link copied!'
            : linkCopied === 'fail'
              ? 'Copy failed'
              : 'Copy link'}
        </button>
      )}
    </div>
  )
}
